import { useMutation, useQueryClient } from '@tanstack/react-query'
import { selectWinningOffer } from 'api/procurement.api'
import { Input } from 'components/Form'
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from 'components/ModalV2/Modal'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { Button, Flex, OutlineButton } from 'styling/GlobalStyles'
import { BigText, NormalText } from 'styling/TypographyV2'
import {
  ProcurementPurchaseRequestQuoteResponse,
  Supplier,
  SupplierStatus,
} from 'types/procurement'

export const SelectWinningOfferModal = ({
  offer,
  onClose,
  id,
}: {
  offer: ProcurementPurchaseRequestQuoteResponse
  onClose: () => void
  id: number | string
}) => {
  const [values, setValues] = useState({
    name: offer.supplierName,
    phoneNumber: '',
    email: '',
    address: '',
    vatId: '',
  })
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (shouldAddSupplier: boolean) => {
      if (shouldAddSupplier) {
        const dataToSend: Omit<Supplier, 'id'> = {
          name: values.name,
          status: SupplierStatus.Active,
        }
        if (values.phoneNumber) {
          dataToSend.phoneNumber = values.phoneNumber
        }
        if (values.email) {
          dataToSend.email = values.email
        }
        if (values.address) {
          dataToSend.address = values.address
        }
        if (values.vatId) {
          dataToSend.vatId = values.vatId
        }
        return selectWinningOffer(id, offer.id, {
          shouldAddSupplier: true,
          supplier: dataToSend,
        })
      }
      return selectWinningOffer(id, offer.id, {
        shouldAddSupplier: false,
      })
    },
    onSuccess: () => {
      toast.success('Winning offer selected successfully')
      queryClient.invalidateQueries({ queryKey: ['procurement-details', id] })

      onClose()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  if (offer.supplier)
    return (
      <Modal
        onClose={() => {
          onClose()
        }}
      >
        <ModalContent>
          <ModalHeader>
            <BigText>Select Winning Offer</BigText>
          </ModalHeader>
          <ModalBody>
            <NormalText>Are you sure you want to select this offer?</NormalText>
          </ModalBody>
          <ModalFooter>
            <Flex column gap='0.5rem' width='100%'>
              <Flex gap='1rem' width='100%'>
                <Button width100>Cancel</Button>
                <Button width100 orange onClick={() => mutation.mutate(false)}>
                  Select
                </Button>
              </Flex>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    )

  return (
    <Modal
      onClose={() => {
        onClose()
      }}
    >
      <ModalContent>
        <ModalHeader>
          <BigText>Select Winning Offer</BigText>
        </ModalHeader>
        <ModalBody>
          <Flex column gap='1rem'>
            <Input
              label='Supplier Name'
              name='supplierName'
              placeholder='Enter supplier name'
              value={values.name}
              onChange={(e) => setValues({ ...values, name: e.target.value })}
              readOnly
              disabled
            />
            <Input
              label='Phone Number'
              name='phoneNumber'
              placeholder='Enter phone number'
              value={values.phoneNumber}
              onChange={(e) => setValues({ ...values, phoneNumber: e.target.value })}
            />
            <Input
              label='Email'
              name='email'
              placeholder='Enter email'
              value={values.email}
              onChange={(e) => setValues({ ...values, email: e.target.value })}
            />
            <Input
              label='Address'
              name='address'
              placeholder='Enter address'
              value={values.address}
              onChange={(e) => setValues({ ...values, address: e.target.value })}
            />
            <Input
              label='VAT ID'
              name='vatId'
              placeholder='Enter VAT ID'
              value={values.vatId}
              onChange={(e) => setValues({ ...values, vatId: e.target.value })}
            />
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Flex column gap='1rem' width='100%'>
            <Flex gap='1rem' width='100%'>
              <Button width100>Cancel</Button>
              <OutlineButton width='100%' onClick={() => mutation.mutate(false)}>
                Select
              </OutlineButton>
            </Flex>
            <Button orange width100 onClick={() => mutation.mutate(true)}>
              Select and create new supplier
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
